import axios, { AxiosInstance } from 'axios';
import { PhotoWithUrls } from '../types';

interface PhotoResponse {
  photos: PhotoWithUrls[];
  hasMore: boolean;
}

class BoxApiService {
  private api: AxiosInstance;

  constructor() {
    // Use the Lambda backend URL from environment or default for local development
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Note: Authentication is handled transparently by the Lambda backend

  async getImageFiles(folderId: string = '0', limit: number = 20, offset: number = 0): Promise<PhotoWithUrls[]> {
    try {
      const response = await this.api.get<PhotoResponse>('/photos', {
        params: {
          folderId,
          limit,
          offset
        }
      });
      return response.data.photos;
    } catch (error) {
      console.error('Error fetching photos:', error);
      throw error;
    }
  }

  async getFileDownloadUrl(fileId: string): Promise<string> {
    try {
      const response = await this.api.get(`/file/${fileId}/download`);
      return response.data.downloadUrl;
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  async getFileThumbnail(fileId: string, maxWidth: number = 320, maxHeight: number = 320): Promise<{ url: string; status: 'ready' | 'processing' | 'unavailable'; retryAfter?: number }> {
    try {
      const response = await this.api.get(`/file/${fileId}/thumbnail`, {
        params: {
          maxWidth,
          maxHeight
        }
      });
      return {
        url: response.data.thumbnailUrl,
        status: response.data.status || 'ready',
        retryAfter: response.data.retryAfter
      };
    } catch (error) {
      console.error('Error getting thumbnail URL:', error);
      // Return unavailable status instead of throwing
      return {
        url: `${process.env.PUBLIC_URL || ''}/placeholders/image-error.svg`,
        status: 'unavailable'
      };
    }
  }

  // Helper method to check if more photos are available
  async hasMorePhotos(folderId: string = '0', limit: number = 20, offset: number = 0): Promise<boolean> {
    try {
      const response = await this.api.get<PhotoResponse>('/photos', {
        params: {
          folderId,
          limit,
          offset
        }
      });
      return response.data.hasMore;
    } catch (error) {
      console.error('Error checking for more photos:', error);
      return false;
    }
  }

  async getFileInfo(fileId: string): Promise<PhotoWithUrls | null> {
    try {
      const response = await this.api.get(`/file/${fileId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log(`File ${fileId} not found`);
        return null;
      }
      console.error('Error getting file info:', error);
      throw error;
    }
  }


}

export const boxApiService = new BoxApiService();